// Content blocks added after the basics, all Material 3 role colours (container + on-container pairs). One component per block type,
// registered in window.Components.ExtraBlocks[type] and rendered by CaseStudyPage:
//   callout {kind,h,x,icon} · button {label,href,to,look,align,icon} · stats {items[{v,l}]} · compare {a,b,al,bl,c}
//   steps {items[{h,x}]} · accordion {items[{h,x}]} · embed {u,c} · audio {u,h,c} · team {items[{n,r,link}]}
// window.EmbedLink.parse(text) turns a pasted page link (or <iframe> snippet) into { src, service, height } for the allow-listed
// providers; the server accepts only the embed forms (EMBED_HOSTS in apps/portal/portfolio-store.js).
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState } = React;
  const rich = t => { const L = window.Components.Linkified; return L ? <L text={String(t)} /> : String(t); };
  const paragraphs = t => String(t || '').split(/\n{2,}/).filter(Boolean);

  // ---- callout ----------------------------------------------------------------------------------------------------------
  const CALLOUT = {
    info: { icon: 'info', bg: 'var(--m3-secondary-container)', fg: 'var(--m3-on-secondary-container)' },
    tip: { icon: 'lightbulb', bg: 'var(--m3-tertiary-container)', fg: 'var(--m3-on-tertiary-container)' },
    warning: { icon: 'warning', bg: 'var(--m3-error-container)', fg: 'var(--m3-on-error-container)' },
    success: { icon: 'check_circle', bg: 'var(--m3-success-container)', fg: 'var(--m3-on-success-container)' }
  };
  function Callout({ b }) {
    const { GoogleIcon } = window.Components;
    const k = CALLOUT[b.kind] || CALLOUT.info;
    if (!b.h && !b.x) return null;
    return (
      <Box role="note" sx={{ my: 3, display: 'flex', gap: 2, p: { xs: 2, md: 2.5 }, borderRadius: 'var(--m3-shape-large)', backgroundColor: k.bg, color: k.fg }}>
        <GoogleIcon name={b.icon || k.icon} size={24} filled style={{ color: 'inherit', flexShrink: 0, marginTop: 2 }} />
        <Box sx={{ minWidth: 0 }}>
          {b.h && <Typography variant="subtitle1" component="p" sx={{ m: 0, fontWeight: 600, color: 'inherit' }}>{b.h}</Typography>}
          {paragraphs(b.x).map((x, n) => <Typography key={n} variant="body1" sx={{ m: 0, mt: b.h || n ? 0.75 : 0, color: 'inherit', whiteSpace: 'pre-line' }}>{rich(x.trim())}</Typography>)}
        </Box>
      </Box>
    );
  }

  // ---- button -----------------------------------------------------------------------------------------------------------
  function ActionButton({ b }) {
    const { Button, GoogleIcon } = window.Components;
    const href = (b.to && window.SiteLinks.href(b.to)) || b.href; // a site link (page, section, article, photo set) or an address
    if (!b.label || !href) return null;
    const external = /^https:/.test(href);
    return (
      <Box sx={{ my: 3, display: 'flex', justifyContent: { left: 'flex-start', center: 'center', right: 'flex-end' }[b.align] || 'flex-start' }}>
        <Button variant={b.look || 'filled'} href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          icon={b.icon ? <GoogleIcon name={b.icon} size={18} /> : undefined}
          trailingIcon={external && !b.icon ? <GoogleIcon name="arrow_outward" size={18} /> : undefined}>{b.label}</Button>
      </Box>
    );
  }

  // ---- stats ------------------------------------------------------------------------------------------------------------
  function Stats({ b }) {
    const items = (b.items || []).filter(i => i.v || i.l);
    if (!items.length) return null;
    const cols = Math.min(items.length, 3);
    return (
      <Box sx={{ my: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: items.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr', sm: `repeat(${cols}, minmax(0, 1fr))`, md: `repeat(${Math.min(items.length, 4)}, minmax(0, 1fr))` } }}>
        {items.map((it, n) => (
          <Box key={n} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-surface-container-high)' }}>
            <Typography variant="h4" component="p" sx={{ m: 0, color: 'var(--m3-primary)', fontWeight: 500, lineHeight: 1.15, overflowWrap: 'anywhere' }}>{it.v}</Typography>
            {it.l && <Typography variant="body2" sx={{ mt: 0.75, color: 'var(--m3-on-surface-variant)' }}>{it.l}</Typography>}
          </Box>
        ))}
      </Box>
    );
  }

  // ---- before / after ---------------------------------------------------------------------------------------------------
  function Compare({ b }) {
    const { GoogleIcon } = window.Components;
    const [pos, setPos] = useState(50);
    if (!b.a || !b.b) return null;
    const tag = (text, side) => text && (
      <Box sx={{ position: 'absolute', top: 12, [side]: 12, px: 1.5, py: 0.5, borderRadius: 'var(--m3-shape-full)', fontSize: 12, fontWeight: 500, backgroundColor: 'var(--m3-surface-container-highest)', color: 'var(--m3-on-surface)', opacity: 0.92, pointerEvents: 'none' }}>{text}</Box>
    );
    const img = (src, extra) => <Box component="img" src={src} alt="" draggable={false} loading="lazy" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', ...extra }} />;
    return (
      <Box component="figure" sx={{ my: 3, mx: 0 }}>
        <Box sx={{ position: 'relative', aspectRatio: '16 / 10', overflow: 'hidden', borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-surface-container-highest)', userSelect: 'none', '&:focus-within': { outline: '3px solid var(--m3-primary)', outlineOffset: 2 } }}>
          {img(b.b)}
          {img(b.a, { clipPath: `inset(0 ${100 - pos}% 0 0)` })}
          {tag(b.al || 'Before', 'left')}
          {tag(b.bl || 'After', 'right')}
          <Box aria-hidden sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 3, ml: '-1.5px', backgroundColor: 'var(--m3-primary)', pointerEvents: 'none' }}>
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: 44, height: 44, transform: 'translate(-50%, -50%)', borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: 'var(--m3-primary)', color: 'var(--m3-on-primary)', boxShadow: 'var(--m3-elevation-2, 0 2px 6px rgba(0,0,0,.3))' }}>
              <GoogleIcon name="code" size={22} style={{ color: 'inherit' }} />
            </Box>
          </Box>
          <Box component="input" type="range" min={0} max={100} step={1} value={pos} onChange={e => setPos(Number(e.target.value))}
            aria-label={`Comparison position, ${b.al || 'Before'} to ${b.bl || 'After'}`}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', m: 0, opacity: 0, cursor: 'ew-resize', touchAction: 'pan-y' }} />
        </Box>
        {b.c && <Typography component="figcaption" variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{b.c}</Typography>}
      </Box>
    );
  }

  // ---- steps (timeline) -------------------------------------------------------------------------------------------------
  function Steps({ b }) {
    const items = (b.items || []).filter(i => i.h || i.x);
    if (!items.length) return null;
    return (
      <Box component="ol" sx={{ listStyle: 'none', m: 0, my: 3, p: 0 }}>
        {items.map((it, n) => (
          <Box component="li" key={n} sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 600, backgroundColor: 'var(--m3-primary-container)', color: 'var(--m3-on-primary-container)' }}>{n + 1}</Box>
              {n < items.length - 1 && <Box sx={{ flex: 1, width: 2, my: 0.5, backgroundColor: 'var(--m3-outline-variant)' }} />}
            </Box>
            <Box sx={{ pb: n < items.length - 1 ? 3 : 0, pt: 0.5, minWidth: 0 }}>
              {it.h && <Typography variant="subtitle1" component="h4" sx={{ m: 0, fontWeight: 600 }}>{it.h}</Typography>}
              {paragraphs(it.x).map((x, k) => <Typography key={k} variant="body1" sx={{ m: 0, mt: 0.5, color: 'var(--m3-on-surface-variant)', whiteSpace: 'pre-line' }}>{rich(x.trim())}</Typography>)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // ---- accordion --------------------------------------------------------------------------------------------------------
  function AccordionBlock({ b }) {
    const { Accordion } = window.Components;
    const items = (b.items || []).filter(i => i.h || i.x);
    if (!items.length) return null;
    return (
      <Box sx={{ my: 3, borderTop: '1px solid var(--m3-outline-variant)' }}>
        {items.map((it, n) => (
          <Box key={n} sx={{ borderBottom: '1px solid var(--m3-outline-variant)', py: 0.5 }}>
            <Accordion title={it.h || `Item ${n + 1}`}>
              <Box sx={{ pb: 1.5 }}>
                {paragraphs(it.x).map((x, k) => <Typography key={k} variant="body1" sx={{ m: 0, mb: 1, color: 'var(--m3-on-surface-variant)', whiteSpace: 'pre-line' }}>{rich(x.trim())}</Typography>)}
              </Box>
            </Accordion>
          </Box>
        ))}
      </Box>
    );
  }

  // ---- embed ------------------------------------------------------------------------------------------------------------
  const EmbedLink = {
    // text: a page link, an embed link or an <iframe ... src="..."> snippet -> { src, service, height } | null
    parse(text) {
      let s = String(text || '').trim();
      const frame = s.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
      if (frame) s = frame[1].replace(/&amp;/g, '&');
      let m;
      if ((m = s.match(/^https:\/\/codepen\.io\/([\w-]+)\/(?:pen|embed|full|details)\/([\w-]+)/))) return { src: `https://codepen.io/${m[1]}/embed/${m[2]}?default-tab=result`, service: 'CodePen', height: 420 };
      if ((m = s.match(/^https:\/\/open\.spotify\.com\/(?:embed\/)?(track|album|playlist|episode|show|artist)\/(\w+)/))) return { src: `https://open.spotify.com/embed/${m[1]}/${m[2]}`, service: 'Spotify', height: m[1] === 'track' || m[1] === 'episode' ? 152 : 352 };
      if (/^https:\/\/w\.soundcloud\.com\/player\/\?\S+$/.test(s)) return { src: s, service: 'SoundCloud', height: 166 };
      if (/^https:\/\/soundcloud\.com\/[\w-]+\/\S+/.test(s)) return { src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(s.split('?')[0])}`, service: 'SoundCloud', height: 166 };
      if (/^https:\/\/www\.google\.com\/maps\/embed\?\S+$/.test(s)) return { src: s, service: 'Google Maps', height: 420 };
      if ((m = s.match(/^https:\/\/www\.loom\.com\/(?:share|embed)\/(\w+)/))) return { src: `https://www.loom.com/embed/${m[1]}`, service: 'Loom', height: 0 };
      return null;
    }
  };
  window.EmbedLink = EmbedLink;

  function Embed({ b }) {
    const { ConsentGate } = window.Components;
    const e = EmbedLink.parse(b.u);
    if (!e) return null;
    return (
      <Box component="figure" sx={{ my: 3, mx: 0 }}>
        <ConsentGate service={e.service} href={e.src} minHeight={e.height || 320}>
          <Box component="iframe" src={e.src} title={b.c || `${e.service} embed`} loading="lazy" allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; fullscreen; clipboard-write"
            sx={{ width: '100%', border: 0, display: 'block', borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-surface-container-highest)', ...(e.height ? { height: e.height } : { aspectRatio: '16 / 9' }) }} />
        </ConsentGate>
        {b.c && <Typography component="figcaption" variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{b.c}</Typography>}
      </Box>
    );
  }

  // ---- audio ------------------------------------------------------------------------------------------------------------
  function Audio({ b }) {
    const { IconTile } = window.Components;
    if (!b.u) return null;
    return (
      <Box sx={{ my: 3, p: { xs: 2, md: 2.5 }, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: 'var(--m3-surface-container)' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
          <IconTile icon="audiotrack" tone="primary" size="md" shape="large" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h4" sx={{ m: 0, overflowWrap: 'anywhere' }}>{b.h || (window.FileInfo ? window.FileInfo.nameOf(b.u) : 'Audio')}</Typography>
            {b.c && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{b.c}</Typography>}
          </Box>
        </Box>
        <Box component="audio" controls preload="none" src={b.u} sx={{ width: '100%', display: 'block' }} />
      </Box>
    );
  }

  // ---- team / credits ---------------------------------------------------------------------------------------------------
  const initials = n => String(n || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const TONE = ['primary', 'secondary', 'tertiary'];
  function Team({ b }) {
    const items = (b.items || []).filter(i => i.n || i.r);
    if (!items.length) return null;
    return (
      <Box sx={{ my: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' } }}>
        {items.map((p, n) => {
          const tone = TONE[n % 3];
          const body = (
            <>
              <Box aria-hidden sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 600, backgroundColor: `var(--m3-${tone}-container)`, color: `var(--m3-on-${tone}-container)` }}>{initials(p.n)}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" component="p" sx={{ m: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>{p.n}</Typography>
                {p.r && <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)' }}>{p.r}</Typography>}
              </Box>
            </>
          );
          const sx = { display: 'flex', gap: 1.5, alignItems: 'center', p: 1.5, borderRadius: 'var(--m3-shape-large)', backgroundColor: 'var(--m3-surface-container-low)', border: '1px solid var(--m3-outline-variant)', color: 'inherit', textDecoration: 'none' };
          return p.link
            ? <Box key={n} component="a" href={p.link} {...(/^https:/.test(p.link) ? { target: '_blank', rel: 'noopener noreferrer' } : {})} sx={{ ...sx, '&:hover': { backgroundColor: 'var(--m3-surface-container)' } }}>{body}</Box>
            : <Box key={n} sx={sx}>{body}</Box>;
        })}
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.ExtraBlocks = { callout: Callout, button: ActionButton, stats: Stats, compare: Compare, steps: Steps, accordion: AccordionBlock, embed: Embed, audio: Audio, team: Team };
})();
